import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
	Card,
	CardBody,
	CardText,
	CardTitle,
	Col,
	Modal,
	ModalBody,
	ModalHeader,
	Row,
} from 'reactstrap';
import { downloadFile, getFile } from '../../../../slices/thunks';
import { getBreachRecordList } from '../../../../slices/breachRecord/thunk';
import OPlaceholder from '../../../../Components/OElements/OPlaceholder';
import { Icons } from '../../../../utils/Icons';

const normalizeFileIds = value => {
	const rawValues = Array.isArray(value)
		? value
		: value
			? [value]
			: [];

	return rawValues
		.map(item => {
			if (!item) return null;
			if (typeof item === 'string') return item;
			return item._id || item.id || null;
		})
		.filter(Boolean);
};

const normalizeFileObjects = value => {
	const rawValues = Array.isArray(value)
		? value
		: value
			? [value]
			: [];

	return rawValues
		.filter(Boolean)
		.map(file => ({ ...file, _id: file?._id || file?.id }))
		.filter(file => file?._id);
};

const mergeUniqueFiles = files => {
	const map = new Map();
	(files || []).forEach(file => {
		if (!file) return;
		const id = file._id || file.id;
		if (!id || map.has(id)) return;
		map.set(id, { ...file, _id: id });
	});
	return Array.from(map.values());
};

const FiscalizacionFilesModal = ({ dataFiscalizacion, isOpen, handleToggleModal }) => {
	const dispatch = useDispatch();

	const [fileData, setFileData] = useState([]);
	const [loading, setLoading] = useState(false);
	const [closureFileData, setClosureFileData] = useState([]);
	const [closureLoading, setClosureLoading] = useState(false);
	const [incumplimientoClosureFiles, setIncumplimientoClosureFiles] = useState([]);
	const [incumplimientoClosureLoading, setIncumplimientoClosureLoading] = useState(false);
	const [fileDownloadLoading, setFileDownloadLoading] = useState(null);
	const [filePreviewLoading, setFilePreviewLoading] = useState(null);
	const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
	const [previewFile, setPreviewFile] = useState(null);

	useEffect(() => {
		const fetchFile = async () => {
			const localFiles = normalizeFileObjects(dataFiscalizacion?.file_data);
			if (localFiles.length) {
				setFileData(localFiles);
				return;
			}

			const fileIds = normalizeFileIds(dataFiscalizacion?.file_id);
			if (!fileIds.length) {
				setFileData([]);
				return;
			}
			setLoading(true);
			try {
				const files = await Promise.all(
					fileIds.map(fileId =>
						dispatch(getFile(fileId)).then(res => {
							const payload = res.payload;
							return payload ? { ...payload, _id: payload._id || payload.id || fileId } : null;
						})
					)
				);
				setFileData(mergeUniqueFiles(files));
			} catch (error) {
				console.error('Error fetching file:', error);
				setFileData([]);
			} finally {
				setLoading(false);
			}
		};
		fetchFile();
	}, [dataFiscalizacion?.file_id, dataFiscalizacion?.file_data, dispatch]);

	useEffect(() => {
		const fetchClosureFile = async () => {
			const closureIds = normalizeFileIds(dataFiscalizacion?.finding_closure_file_id);
			if (!closureIds.length) {
				setClosureFileData([]);
				return;
			}
			setClosureLoading(true);
			try {
				const files = await Promise.all(
					closureIds.map(fileId =>
						dispatch(getFile(fileId)).then(res => {
							const payload = res.payload;
							return payload ? { ...payload, _id: payload._id || payload.id || fileId } : null;
						})
					)
				);
				setClosureFileData(mergeUniqueFiles(files));
			} catch (error) {
				console.error('Error fetching closure file:', error);
				setClosureFileData([]);
			} finally {
				setClosureLoading(false);
			}
		};
		fetchClosureFile();
	}, [dataFiscalizacion?.finding_closure_file_id, dispatch]);

	useEffect(() => {
		const inspectionId = dataFiscalizacion?._id;
		if (!isOpen || !inspectionId) {
			setIncumplimientoClosureFiles([]);
			return;
		}
		setIncumplimientoClosureLoading(true);
		dispatch(getBreachRecordList({
			filters: {
				disable: { $eq: false },
				inspections_id: { $eq: inspectionId },
			},
			fields: ['file_id', 'file_data', 'finding_closure_file_id'],
			pagination: { page: 1, pageSize: 1000 },
		}))
			.unwrap()
			.then(records => {
				const safeRecords = Array.isArray(records) ? records : [];
				if (!safeRecords.length) {
					setIncumplimientoClosureFiles([]);
					return;
				}

				const localFiles = mergeUniqueFiles(
					safeRecords.flatMap(record => normalizeFileObjects(record?.file_data))
				);

				const idFiles = mergeUniqueFiles(
					safeRecords.flatMap(record => [
						...normalizeFileIds(record?.file_id),
						...normalizeFileIds(record?.finding_closure_file_id),
					].map(fileId => ({ _id: fileId })))
				);

				const idsToFetch = idFiles
					.map(file => file._id)
					.filter(fileId => !localFiles.some(localFile => localFile._id === fileId));

				if (!idsToFetch.length) {
					setIncumplimientoClosureFiles(localFiles);
					return;
				}

				return Promise.all(
					idsToFetch.map(fileId =>
						dispatch(getFile(fileId)).then(res => {
							const payload = res.payload;
							return payload ? { ...payload, _id: payload._id || payload.id || fileId } : null;
						})
					)
				).then(results => {
					setIncumplimientoClosureFiles(mergeUniqueFiles([...localFiles, ...results.filter(Boolean)]));
				});
			})
			.catch(err => console.error('Error fetching incumplimiento closure files:', err))
			.finally(() => setIncumplimientoClosureLoading(false));
	}, [isOpen, dataFiscalizacion?._id, dispatch]);

	const handleDownloadFile = file => {
		if (!file?._id || fileDownloadLoading) return;
		setFileDownloadLoading(file._id);
		dispatch(downloadFile({ file_id: file._id }))
			.then(data => {
				const payload = data.payload;
				const url = window.URL.createObjectURL(payload);
				const link = document.createElement('a');
				link.href = url;
				link.setAttribute('download', `${file.filename}${file.extension}`);
				document.body.appendChild(link);
				link.click();
				link.remove();
			})
			.finally(() => setFileDownloadLoading(null));
	};

	const handlePreviewClick = async file => {
		if (!file?._id || filePreviewLoading) return;
		setFilePreviewLoading(file._id);
		try {
			const result = await dispatch(downloadFile({ file_id: file._id, disable_msg: true }));
			const blob = result.payload;
			if (!(blob instanceof Blob)) {
				throw new Error('El archivo no es un Blob válido.');
			}
			const fileURL = URL.createObjectURL(blob);
			setPreviewFile({ ...file, url: fileURL });
			setIsPreviewModalOpen(true);
		} catch (error) {
			console.error('Error al obtener el archivo:', error);
		} finally {
			setFilePreviewLoading(null);
		}
	};

	const handleClosePreviewModal = () => {
		if (previewFile?.url) URL.revokeObjectURL(previewFile.url);
		setPreviewFile(null);
		setIsPreviewModalOpen(false);
	};

	const renderFileActions = file => (
		<div className='d-flex align-items-end justify-content-end'>
			<div className='flex-shrink-0'>
				<div className='d-flex flex-wrap gap-2'>
					<button
						className='btn btn-label btn-primary'
						onClick={() => handleDownloadFile(file)}
						disabled={Boolean(fileDownloadLoading)}
						style={{ width: 110 }}
					>
						{fileDownloadLoading === file?._id ? (
							<div className='d-flex justify-content-center'>
								<span className='spinner-border spinner-border-sm' role='status' aria-hidden='true'></span>
							</div>
						) : (
							<><i className='icon-download label-icon'></i>{' '}Descargar</>
						)}
					</button>
					<button
						className='btn btn-label btn-primary'
						onClick={() => handlePreviewClick(file)}
						disabled={Boolean(filePreviewLoading)}
						style={{ width: 130 }}
					>
						{filePreviewLoading === file?._id ? (
							<div className='d-flex justify-content-center'>
								<span className='spinner-border spinner-border-sm' role='status' aria-hidden='true'></span>
							</div>
						) : (
							<><i className='ri-eye-line label-icon'></i>{' '}Previsualizar</>
						)}
					</button>
				</div>
			</div>
		</div>
	);

	return (
		<div>
			<Modal
				isOpen={isOpen}
				toggle={handleToggleModal}
				size='lg'
				className='border-0'
				modalClassName='modal fade zoomIn'
				backdrop='static'
				centered={true}
			>
				<ModalHeader toggle={handleToggleModal}>Documentos Adjuntos</ModalHeader>
				<ModalBody>
					<div className='d-flex flex-wrap gap-3 align-items-start'>
						<div style={{ flex: '0 0 300px', maxWidth: '100%' }}>
							{fileData.length > 0 && <h6 className='text-muted fw-semibold mb-2'>Documento de Acta de Obervaciones</h6>}
							{loading ? (
								<OPlaceholder />
							) : fileData.length > 0 ? (
								fileData.map((file, idx) => (
									<Card className='border mb-3' key={file._id || idx}>
										<CardBody>
											<Row>
												<Col md={2}>
													<i className={`${Icons.guide} ri-3x`}></i>
												</Col>
												<Col>
													<CardTitle tag='h5' className='mt-2'>
														{file?.file_type?.name || 'Documento'}
													</CardTitle>
													<CardText>{file?.filename}</CardText>
												</Col>
											</Row>
										</CardBody>
										<CardBody>
												{renderFileActions(file)}
										</CardBody>
									</Card>
								))
							) : null}
						</div>

						<div style={{ flex: '0 0 300px', maxWidth: '100%' }}>
							{closureFileData.length > 0 && <h6 className='text-muted fw-semibold mb-2'>Respaldo de Cierre Sin Hallazgos</h6>}
							{closureLoading ? (
								<OPlaceholder />
							) : closureFileData.length > 0 ? (
								closureFileData.map((file, idx) => (
									<Card className='border mb-3' key={file._id || idx}>
										<CardBody>
											<Row>
												<Col md={2}>
													<i className={`${Icons.guide} ri-3x`}></i>
												</Col>
												<Col>
													<CardTitle tag='h5' className='mt-2'>
														{file?.file_type?.name || 'Documento'}
													</CardTitle>
													<CardText>{file?.filename}</CardText>
												</Col>
											</Row>
										</CardBody>
										<CardBody>
												{renderFileActions(file)}
										</CardBody>
									</Card>
								))
							) : null}
						</div>

						<div style={{ flex: '0 0 300px', maxWidth: '100%' }}>
							{incumplimientoClosureFiles.length > 0 && (
								<h6 className='text-muted fw-semibold mb-2'>Archivos — Incumplimientos</h6>
							)}
							{incumplimientoClosureLoading ? (
								<OPlaceholder />
							) : incumplimientoClosureFiles.length > 0 ? (
								incumplimientoClosureFiles.map((file, idx) => (
									<Card className='border mb-3' key={file._id || idx}>
										<CardBody>
											<Row>
												<Col md={2}><i className={`${Icons.guide} ri-3x`}></i></Col>
												<Col>
													<CardTitle tag='h5' className='mt-2'>{file?.file_type?.name || 'Documento'}</CardTitle>
													<CardText>{file?.filename}</CardText>
												</Col>
											</Row>
										</CardBody>
										<CardBody>
												{renderFileActions(file)}
										</CardBody>
									</Card>
								))
							) : null}
						</div>
					</div>
				</ModalBody>
			</Modal>

			<Modal
				isOpen={isPreviewModalOpen}
				toggle={handleClosePreviewModal}
				centered={false}
				className='w-100 p-4'
				fullscreen={true}
				scrollable={false}
			>
				<ModalHeader toggle={handleClosePreviewModal}>
					Previsualización
				</ModalHeader>
				<ModalBody className='overflow-hidden'>
					{previewFile && previewFile.url ? (
						previewFile.mime_type?.includes('image') ? (
							<img
								src={previewFile.url}
								alt={previewFile.filename}
								className='img-fluid'
							/>
						) : previewFile.mime_type?.includes('pdf') ? (
							<iframe src={previewFile.url} width='100%' height='100%' />
						) : previewFile.mime_type?.includes('text') ? (
							<pre>{previewFile.content || 'No se pudo cargar el contenido'}</pre>
						) : (
							<p>Formato no compatible para previsualización.</p>
						)
					) : (
						<p>Cargando...</p>
					)}
				</ModalBody>
			</Modal>
		</div>
	);
};

export default FiscalizacionFilesModal;
